<template>
    <div v-if="!isLoading">
        <div class="mt-3 text-left sm:mt-5">
            <p class="text-gray-500">
                Select table areas where this category will not be available.
                If no areas are selected, the category will be available in all areas.
            </p>
            <div class="mt-4">
                <vue-multi-select
                    v-model="selectedVenueAreaIds"
                    :options="venueAreaOptions"
                    :multiple="true"
                    :searchable="true"
                    placeholder="Select table areas"
                    label="name"
                    track-by="id"
                    :show-labels="false"
                    class="sk-multiselect"
                >
                    <template v-slot:singleLabel="props">
                        <span class="option__desc">
                            <span class="option__title">{{ props.option.name }}</span>
                        </span>
                    </template>

                    <template v-slot:option="props">
                        <div class="option__desc">
                            <span class="option__title">{{ props.option.name }}</span>
                        </div>
                    </template>
                </vue-multi-select>
            </div>
        </div>
        <div class="mt-5 justify-between sm:mt-6 sm:flex sm:w-auto">
            <span class="flex rounded-md shadow-sm">
                <button
                    @click="saveRestrictions"
                    :disabled="isLoading"
                    type="button"
                    class="focus:outline-none inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium leading-6 text-white shadow-sm transition duration-150 ease-in-out hover:bg-indigo-500 focus:border-indigo-700 focus:shadow-outline-indigo sm:text-sm sm:leading-5"
                >
                    Save changes
                </button>
            </span>
            <span
                class="mt-3 flex w-full rounded-md shadow-sm sm:ml-3 sm:mt-0 sm:w-auto"
            >
                <button
                    @click="close"
                    type="button"
                    class="focus:outline-none inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium leading-6 text-gray-700 shadow-sm transition duration-150 ease-in-out hover:text-gray-500 focus:border-blue-300 focus:shadow-outline sm:text-sm sm:leading-5"
                >
                    Close
                </button>
            </span>
        </div>
    </div>
    <div v-else class="mt-20 spinner"></div>
</template>

<script>
import VueMultiSelect from 'vue-multiselect';

export default {
    name: 'CategoryTableRestrictionsSettings',
    components: {
        VueMultiSelect
    },
    props: {
        category: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            isLoading: false,
            selectedVenueAreaIds: [], // Will hold the full area objects, not just IDs
            venueAreas: []
        };
    },
    computed: {
        venueAreaOptions() {
            return this.venueAreas.map(area => ({
                id: area.id,
                name: area.name
            }));
        }
    },
    async mounted() {
        this.isLoading = true;
        try {
            // Important: First fetch venue areas, then fetch restrictions
            // since fetchAreaRestrictions depends on venueAreas being populated
            await this.fetchVenueAreas();
            await this.fetchAreaRestrictions();
        } catch (error) {
            this.$notify({
                group: 'settings',
                title: 'An error occurred',
                text: 'Failed to load table areas'
            });
        } finally {
            this.isLoading = false;
        }
    },
    methods: {
        close() {
            this.$emit('close');
        },
        update() {
            this.$emit('update');
        },
        async fetchVenueAreas() {
            // Get the venue ID from the category
            const { data: menu } = await this.$axios.get(`/menus/${this.category.menuId}`);
            const venueId = menu.menu.venueId;
            
            // Fetch venue tables
            const { data } = await this.$axios.get(`/venues/${venueId}/tables?cache=false`);
            this.venueAreas = data.filter(area => area.active);
        },
        async fetchAreaRestrictions() {
            try {
                const { data } = await this.$axios.get(`/categories/${this.category.id}/area-restrictions`);
                // Handle the payload structure with restrictedAreas array
                if (data.restrictedAreas && Array.isArray(data.restrictedAreas)) {
                    // Get the restricted area IDs
                    const restrictedAreaIds = data.restrictedAreas.map(area => area.id);
                    
                    // Map the IDs to full area objects from venueAreas
                    this.selectedVenueAreaIds = this.venueAreas
                        .filter(area => restrictedAreaIds.includes(area.id))
                        .map(area => ({
                            id: area.id,
                            name: area.name
                        }));
                } else {
                    this.selectedVenueAreaIds = [];
                }
            } catch (error) {
                this.selectedVenueAreaIds = [];
            }
        },
        async saveRestrictions() {
            this.isLoading = true;
            try {
                // Allow empty selection to match UI text that states
                // "If no areas are selected, the category will be available in all areas"
                
                const venueAreaIds = this.selectedVenueAreaIds.map(area => String(area.id));
                
                try {
                    await this.$axios.post(`/categories/${this.category.id}/area-restrictions`, {
                        venueAreaIds: venueAreaIds
                    });
                } catch (error) {
                    // If we get a validation error, show a more specific message
                    if (error.response && error.response.data && error.response.data.message) {
                        this.$notify({
                            group: 'settings',
                            title: 'Error',
                            text: `Failed to update restrictions: ${error.response.data.message}`
                        });
                        this.isLoading = false;
                        return;
                    }
                    // Re-throw for general error handling
                    throw error;
                }
                
                this.$notify({
                    group: 'settings',
                    title: 'Success',
                    text: 'Table restrictions updated successfully'
                });
                
                this.update();
                this.close();
            } catch (error) {
                let errorMessage = 'Failed to update table restrictions';
                
                // Extract more specific error message if available
                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage += `: ${error.response.data.message}`;
                }
                
                this.$notify({
                    group: 'settings',
                    title: 'An error occurred',
                    text: errorMessage
                });
            } finally {
                this.isLoading = false;
            }
        }
    }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.multiselect {
  min-height: 40px;
}

.multiselect__tags {
  min-height: 40px;
  padding: 8px 40px 0 8px;
  border-radius: 6px;
  border-color: #d1d5db;
}

.multiselect__select {
  height: 38px;
}

.multiselect__input, .multiselect__single {
  font-size: 14px;
  line-height: 20px;
}

.multiselect__option--highlight {
  background: #4f46e5;
}

.multiselect__option--highlight:after {
  background: #4338ca;
}
</style>
